import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { List, Loader2, AlertCircle, Pencil, Trash2 } from 'lucide-react';
import { laborScheduleService } from '../services/laborScheduleService';
import { employeeService } from '../../empleados/services/employeeService';
import {
  formatCalendarOption,
  formatHolidayDate,
  formatOverrideScope,
  formatOverrideSummary,
  isOverrideExpired,
  overrideOverlapsYear,
} from '../utils/laborCalendarUtils';
import { getEmployeeDisplayName } from '../utils/laborFactoryUtils';
import { LaborCalendarModalShell } from './LaborCalendarModalShell';
import { LaborConfirmDialog } from './LaborConfirmDialog';
import { LaborOverrideModal } from './LaborOverrideModal';

export function LaborManageOverridesModal({
  isOpen,
  onClose,
  factoryId,
  factoryCode,
  defaultYear,
  defaultCalendarId = null,
  filterEmployeeId = null,
  onAfterChange,
}) {
  const queryClient = useQueryClient();
  const [calendarId, setCalendarId] = useState('');
  const [includeExpired, setIncludeExpired] = useState(false);
  const [error, setError] = useState(null);
  const [editingOverride, setEditingOverride] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [overrideToDelete, setOverrideToDelete] = useState(null);

  const { data: calendars = [], isLoading: loadingCalendars } = useQuery({
    queryKey: ['labor-calendars', factoryId],
    queryFn: () => laborScheduleService.listCalendarsByFactory(factoryId),
    enabled: isOpen && Boolean(factoryId),
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['labor-calendar-employees', factoryId],
    queryFn: () => employeeService.getEmployees('', true, factoryId),
    enabled: isOpen && Boolean(factoryId),
  });

  const {
    data: overridesRaw = [],
    isLoading: loadingOverrides,
    isFetching: fetchingOverrides,
    refetch,
  } = useQuery({
    queryKey: ['labor-overrides', calendarId, includeExpired],
    queryFn: () => laborScheduleService.listOverrides(calendarId, includeExpired),
    enabled: isOpen && Boolean(calendarId),
  });

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setEditingOverride(null);
    setEditOpen(false);
    setOverrideToDelete(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (defaultCalendarId) {
      setCalendarId(String(defaultCalendarId));
      return;
    }
    if (calendars.length === 0) return;
    const match = calendars.find((c) => Number(c.year) === Number(defaultYear));
    setCalendarId(String((match || calendars[0]).id));
  }, [isOpen, defaultCalendarId, calendars, defaultYear]);

  const employeeNameById = useMemo(() => {
    const map = {};
    employees.forEach((emp) => {
      map[emp.id] = getEmployeeDisplayName(emp);
    });
    return map;
  }, [employees]);

  const overrides = useMemo(() => {
    return [...overridesRaw]
      .filter((ov) => overrideOverlapsYear(ov, defaultYear))
      .filter((ov) => {
        if (!filterEmployeeId) return true;
        return String(ov.employee_id) === String(filterEmployeeId);
      })
      .sort((a, b) =>
        formatHolidayDate(a.start_date).localeCompare(formatHolidayDate(b.start_date))
      );
  }, [overridesRaw, defaultYear, filterEmployeeId]);

  const deleteMutation = useMutation({
    mutationFn: (overrideId) => laborScheduleService.deleteOverride(overrideId),
    onSuccess: async () => {
      setOverrideToDelete(null);
      await queryClient.invalidateQueries({ queryKey: ['labor-overrides'] });
      await queryClient.invalidateQueries({ queryKey: ['labor-calendar-schedule'] });
      onAfterChange?.();
      refetch();
    },
    onError: (err) => {
      setError(err?.message || 'No se pudo eliminar el override');
    },
  });

  const handleDeleteClick = (override) => {
    setError(null);
    setOverrideToDelete(override);
  };

  const handleDeleteConfirm = () => {
    if (!overrideToDelete) return;
    setError(null);
    deleteMutation.mutate(overrideToDelete.id);
  };

  const handleEditSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: ['labor-overrides'] });
    await queryClient.invalidateQueries({ queryKey: ['labor-calendar-schedule'] });
    onAfterChange?.();
    setEditOpen(false);
    setEditingOverride(null);
    refetch();
  };

  const footer = (
    <button
      type="button"
      onClick={onClose}
      className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 text-sm"
    >
      Cerrar
    </button>
  );

  return (
    <>
      <LaborCalendarModalShell
        isOpen={isOpen}
        onClose={onClose}
        title="Overrides del calendario"
        icon={List}
        footer={footer}
        size="lg"
      >
        {error && (
          <div className="mb-4 p-3 rounded-lg border border-red-800 bg-red-900/20 flex gap-2 text-red-300 text-sm">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <p className="text-sm text-gray-400 mb-4">
          Fábrica <span className="text-white font-medium">{factoryCode}</span>
          {filterEmployeeId
            ? ` · filtrado por empleado ID ${filterEmployeeId}`
            : ' · todos los ámbitos'}
          . Año {defaultYear}.
        </p>

        <div className="flex flex-wrap gap-3 items-end mb-4">
          <div className="flex-1 min-w-[200px]">
            <label htmlFor="labor-ov-list-cal" className="block text-sm text-gray-300 mb-2">
              Calendario
            </label>
            {loadingCalendars ? (
              <p className="text-gray-500 text-sm">Cargando…</p>
            ) : (
              <select
                id="labor-ov-list-cal"
                value={calendarId}
                onChange={(e) => setCalendarId(e.target.value)}
                className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
              >
                {calendars.map((cal) => (
                  <option key={cal.id} value={cal.id}>
                    {formatCalendarOption(cal)}
                  </option>
                ))}
              </select>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer pb-2">
            <input
              type="checkbox"
              checked={includeExpired}
              onChange={(e) => setIncludeExpired(e.target.checked)}
              className="rounded border-gray-600"
            />
            Incluir caducados
          </label>
        </div>

        {loadingOverrides || fetchingOverrides ? (
          <p className="text-gray-500 text-sm flex items-center gap-2 py-6 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" />
            Cargando overrides…
          </p>
        ) : overrides.length === 0 ? (
          <p className="text-amber-400 text-sm bg-amber-900/20 border border-amber-700/50 rounded-lg p-4 text-center">
            No hay overrides para este calendario y filtros actuales.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-700">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-900/80 text-gray-400">
                <tr>
                  <th className="px-3 py-2 font-medium">Rango</th>
                  <th className="px-3 py-2 font-medium">Ámbito</th>
                  <th className="px-3 py-2 font-medium">Tipo</th>
                  <th className="px-3 py-2 font-medium">Horario</th>
                  <th className="px-3 py-2 font-medium">Motivo</th>
                  <th className="px-3 py-2 font-medium w-24">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {overrides.map((ov) => {
                  const expired = isOverrideExpired(ov);
                  return (
                    <tr key={ov.id} className={expired ? 'opacity-60' : ''}>
                      <td className="px-3 py-2 text-gray-200 whitespace-nowrap">
                        {formatHolidayDate(ov.start_date)} → {formatHolidayDate(ov.end_date)}
                        {expired && (
                          <span className="ml-1 text-xs text-gray-500">(caducado)</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-300">
                        {formatOverrideScope(ov, employeeNameById)}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs ${
                            ov.is_working_day
                              ? 'bg-emerald-900/40 text-emerald-300'
                              : 'bg-red-900/40 text-red-300'
                          }`}
                        >
                          {ov.is_working_day ? 'Laborable' : 'No laborable'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-400 whitespace-nowrap">
                        {ov.start_time && ov.end_time
                          ? `${ov.start_time}–${ov.end_time}`
                          : '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-400 max-w-[200px] truncate" title={ov.reason}>
                        {ov.reason}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingOverride(ov);
                              setEditOpen(true);
                            }}
                            className="p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-white"
                            title="Editar"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(ov)}
                            disabled={deleteMutation.isPending}
                            className="p-1.5 rounded hover:bg-red-900/40 text-gray-400 hover:text-red-300 disabled:opacity-50"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </LaborCalendarModalShell>

      <LaborConfirmDialog
        isOpen={Boolean(overrideToDelete)}
        onClose={() => !deleteMutation.isPending && setOverrideToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Eliminar override"
        message={
          overrideToDelete
            ? `¿Eliminar este override?\n\n${formatOverrideSummary(overrideToDelete, employeeNameById)}`
            : ''
        }
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        isPending={deleteMutation.isPending}
      />

      <LaborOverrideModal
        isOpen={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditingOverride(null);
        }}
        factoryId={factoryId}
        factoryCode={factoryCode}
        defaultYear={defaultYear}
        defaultCalendarId={calendarId || defaultCalendarId}
        defaultEmployeeId={filterEmployeeId}
        lockEmployeeScope={Boolean(filterEmployeeId)}
        editingOverride={editingOverride}
        onSuccess={handleEditSuccess}
      />
    </>
  );
}
